import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Zap,
  Target,
  Clock,
  Users,
  ShieldAlert,
  AlertCircle,
  Check,
  Plus,
  Trash2,
  RefreshCw,
  Save,
} from 'lucide-react';
import {
  automationApi,
  ScoringWeights,
  SLAConfig,
  DisqualificationRules,
  BlacklistEntry,
} from '../api/client';
import { useRole } from '../contexts/RoleContext';
import { Button } from '../components/common/Button';
import { LeadScoreBadge } from '../components/automation/LeadScoreBadge';
import { SLASummaryCard } from '../components/automation/SLAStatusIndicator';

type TabId = 'scoring' | 'sla' | 'assignment' | 'qualification';

const tabs: { id: TabId; label: string; icon: typeof Target }[] = [
  { id: 'scoring', label: 'Scoring', icon: Target },
  { id: 'sla', label: 'SLA', icon: Clock },
  { id: 'assignment', label: 'Assignment', icon: Users },
  { id: 'qualification', label: 'Qualification', icon: ShieldAlert },
];

function ScoringTab() {
  const queryClient = useQueryClient();
  const { can } = useRole();
  const canConfigure = can('automation:configure');

  const [localWeights, setLocalWeights] = useState<ScoringWeights | null>(null);

  const { data: weightsData, isLoading: weightsLoading } = useQuery({
    queryKey: ['automation', 'scoring_weights'],
    queryFn: () => automationApi.getConfig<ScoringWeights>('scoring_weights'),
  });

  const { data: statsData } = useQuery({
    queryKey: ['automation', 'score', 'stats'],
    queryFn: () => automationApi.getScoreStats(),
  });

  const { data: locationsData } = useQuery({
    queryKey: ['automation', 'locations'],
    queryFn: () => automationApi.getTargetLocations(),
  });

  const updateWeightsMutation = useMutation({
    mutationFn: (weights: ScoringWeights) => automationApi.setConfig('scoring_weights', weights),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation', 'scoring_weights'] });
      setLocalWeights(null);
    },
  });

  const rescoreAllMutation = useMutation({
    mutationFn: () => automationApi.scoreAllLeads(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation', 'score', 'stats'] });
    },
  });

  const weights = localWeights || weightsData?.value;
  const weightsSum = weights
    ? weights.location + weights.cuisine + weights.license + weights.freshness
    : 100;
  const isWeightsValid = weightsSum === 100;

  const handleWeightChange = (key: keyof ScoringWeights, value: number) => {
    if (!weights) return;
    setLocalWeights({ ...weights, [key]: value });
  };

  const handleSaveWeights = () => {
    if (localWeights && isWeightsValid) {
      updateWeightsMutation.mutate(localWeights);
    }
  };

  if (weightsLoading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-primary border-r-transparent" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      {statsData && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{statsData.total}</div>
            <div className="text-sm text-gray-500">Total Leads</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{statsData.scored}</div>
            <div className="text-sm text-gray-500">Scored</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{statsData.average}</div>
            <div className="text-sm text-gray-500">Avg Score</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-2">
            {statsData.distribution.map((d) => (
              <div key={d.range} className="text-xs" title={d.range}>
                <LeadScoreBadge score={d.range.includes('High') ? 85 : d.range.includes('Medium') ? 55 : d.range.includes('Low') ? 25 : null} size="sm" />
                <div className="text-center mt-1">{d.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weights Configuration */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Scoring Weights</h3>
          {canConfigure && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => rescoreAllMutation.mutate()}
                isLoading={rescoreAllMutation.isPending}
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Rescore All
              </Button>
              {localWeights && (
                <Button
                  size="sm"
                  onClick={handleSaveWeights}
                  disabled={!isWeightsValid}
                  isLoading={updateWeightsMutation.isPending}
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save
                </Button>
              )}
            </div>
          )}
        </div>

        {!isWeightsValid && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Weights must sum to 100 (currently {weightsSum})
          </div>
        )}

        <div className="space-y-4">
          {weights && Object.entries(weights).map(([key, value]) => (
            <div key={key} className="flex items-center gap-4">
              <span className="w-24 text-sm font-medium capitalize">{key}</span>
              <input
                type="range"
                min="0"
                max="100"
                value={value}
                onChange={(e) => handleWeightChange(key as keyof ScoringWeights, parseInt(e.target.value))}
                disabled={!canConfigure}
                className="flex-1"
              />
              <span className="w-12 text-right text-sm font-mono">{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Target Locations */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Target Locations</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Score Boost</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {locationsData?.locations.map((loc) => (
                <tr key={loc.id}>
                  <td className="px-4 py-2 text-sm text-gray-600">{loc.location_type}</td>
                  <td className="px-4 py-2 text-sm font-medium">{loc.value}</td>
                  <td className="px-4 py-2 text-sm">{loc.score_boost}</td>
                  <td className="px-4 py-2">
                    {loc.is_active ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SLATab() {
  const queryClient = useQueryClient();
  const { can } = useRole();
  const canConfigure = can('automation:configure');

  const { data: slaStatusData } = useQuery({
    queryKey: ['automation', 'sla', 'status'],
    queryFn: () => automationApi.getSLAStatus(),
  });

  const { data: configData, isLoading } = useQuery({
    queryKey: ['automation', 'sla_config'],
    queryFn: () => automationApi.getConfig<SLAConfig>('sla_config'),
  });

  const triggerCheckMutation = useMutation({
    mutationFn: () => automationApi.triggerSLACheck(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation', 'sla'] });
    },
  });

  if (isLoading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-primary border-r-transparent" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* SLA Summary */}
      {slaStatusData && (
        <div>
          <h3 className="text-lg font-medium mb-4">Current SLA Status</h3>
          <SLASummaryCard summary={slaStatusData} />
          <div className="mt-2 text-sm text-gray-500">
            Total assigned leads: {slaStatusData.total_assigned}
          </div>
        </div>
      )}

      {/* Manual Check */}
      {canConfigure && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={() => triggerCheckMutation.mutate()}
            isLoading={triggerCheckMutation.isPending}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Trigger SLA Check
          </Button>
        </div>
      )}

      {/* SLA Configuration */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">SLA Timeframes (hours)</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Warning</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Escalation</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Return</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Return Enabled</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {configData?.value && Object.entries(configData.value).map(([status, config]) => (
                <tr key={status}>
                  <td className="px-4 py-2 text-sm font-medium capitalize">{status}</td>
                  <td className="px-4 py-2 text-sm">{config.warning_hours}h</td>
                  <td className="px-4 py-2 text-sm">{config.escalation_hours}h</td>
                  <td className="px-4 py-2 text-sm">{config.return_hours ? `${config.return_hours}h` : '-'}</td>
                  <td className="px-4 py-2">
                    {config.return_enabled ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AssignmentTab() {
  const { data: statusData, isLoading } = useQuery({
    queryKey: ['automation', 'assignment', 'status'],
    queryFn: () => automationApi.getAssignmentStatus(),
  });

  const { data: statsData } = useQuery({
    queryKey: ['automation', 'assignment', 'stats'],
    queryFn: () => automationApi.getAssignmentStats(),
  });

  if (isLoading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-primary border-r-transparent" /></div>;
  }

  const config = statusData?.config;

  return (
    <div className="space-y-6">
      {/* Status */}
      <div className={`p-4 rounded-lg ${statusData?.should ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${statusData?.should ? 'bg-green-500' : 'bg-yellow-500'}`} />
          <span className={statusData?.should ? 'text-green-800' : 'text-yellow-800'}>
            Round-robin assignment is {statusData?.should ? 'active' : 'inactive'}
          </span>
        </div>
        {statusData?.reason && (
          <div className="text-sm text-gray-600 mt-1 ml-5">{statusData.reason}</div>
        )}
      </div>

      {/* Stats */}
      {statsData && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{statsData.total_auto_assigned}</div>
            <div className="text-sm text-gray-500">Total Auto-Assigned</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{statsData.today_assigned}</div>
            <div className="text-sm text-gray-500">Assigned Today</div>
          </div>
        </div>
      )}

      {/* Configuration */}
      {config && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium mb-4">Configuration</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Enabled</span>
              <span className="font-medium">{config.enabled ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Working Hours Only</span>
              <span className="font-medium">{config.working_hours_only ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Working Hours</span>
              <span className="font-medium">{config.working_hours.start}:00 - {config.working_hours.end}:00 ({config.working_hours.timezone})</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Exclude Weekends</span>
              <span className="font-medium">{config.exclude_weekends ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Max Leads Per Rep/Day</span>
              <span className="font-medium">{config.max_leads_per_rep_per_day}</span>
            </div>
          </div>
        </div>
      )}

      {/* By Rep */}
      {statsData?.by_rep && statsData.by_rep.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium mb-4">Today's Assignments by Rep</h3>
          <div className="space-y-2">
            {statsData.by_rep.map((r) => (
              <div key={r.rep} className="flex justify-between text-sm">
                <span className="text-gray-600">{r.rep}</span>
                <span className="font-medium">{r.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function QualificationTab() {
  const queryClient = useQueryClient();
  const { can } = useRole();
  const canConfigure = can('automation:configure');

  const [newEntry, setNewEntry] = useState({
    entry_type: 'name' as BlacklistEntry['entry_type'],
    pattern: '',
    match_type: 'contains' as BlacklistEntry['match_type'],
    reason: '',
  });

  const { data: rulesData, isLoading: rulesLoading } = useQuery({
    queryKey: ['automation', 'disqualification_rules'],
    queryFn: () => automationApi.getConfig<DisqualificationRules>('disqualification_rules'),
  });

  const { data: blacklistData, isLoading: blacklistLoading } = useQuery({
    queryKey: ['automation', 'blacklist'],
    queryFn: () => automationApi.getBlacklist(),
  });

  const { data: statsData } = useQuery({
    queryKey: ['automation', 'qualification', 'stats'],
    queryFn: () => automationApi.getQualificationStats(),
  });

  const addEntryMutation = useMutation({
    mutationFn: (entry: typeof newEntry) => automationApi.addBlacklistEntry(entry),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation', 'blacklist'] });
      setNewEntry({ entry_type: 'name', pattern: '', match_type: 'contains', reason: '' });
    },
  });

  const deleteEntryMutation = useMutation({
    mutationFn: (id: number) => automationApi.deleteBlacklistEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation', 'blacklist'] });
    },
  });

  if (rulesLoading || blacklistLoading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-primary border-r-transparent" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      {statsData && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{statsData.total_disqualified}</div>
            <div className="text-sm text-gray-500">Total Disqualified</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{statsData.total_flagged}</div>
            <div className="text-sm text-gray-500">Total Flagged</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{statsData.disqualified_today}</div>
            <div className="text-sm text-gray-500">Disqualified Today</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{statsData.flagged_today}</div>
            <div className="text-sm text-gray-500">Flagged Today</div>
          </div>
        </div>
      )}

      {/* Rules */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Disqualification Rules</h3>
        <div className="space-y-3">
          {rulesData?.value && Object.entries(rulesData.value).map(([key, rule]) => (
            <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-sm">{rule.description}</div>
                <div className="text-xs text-gray-500 mt-1">
                  Action: <span className={rule.action === 'disqualify' ? 'text-red-600' : 'text-yellow-600'}>{rule.action}</span>
                </div>
              </div>
              <div className={`px-2 py-1 rounded text-xs font-medium ${rule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                {rule.enabled ? 'Enabled' : 'Disabled'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Blacklist */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Blacklist</h3>

        {/* Add Entry Form */}
        {canConfigure && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-4 gap-3">
              <select
                value={newEntry.entry_type}
                onChange={(e) => setNewEntry({ ...newEntry, entry_type: e.target.value as BlacklistEntry['entry_type'] })}
                className="input text-sm"
              >
                <option value="name">Name</option>
                <option value="address">Address</option>
                <option value="phone">Phone</option>
                <option value="domain">Domain</option>
              </select>
              <input
                type="text"
                value={newEntry.pattern}
                onChange={(e) => setNewEntry({ ...newEntry, pattern: e.target.value })}
                placeholder="Pattern"
                className="input text-sm"
              />
              <select
                value={newEntry.match_type}
                onChange={(e) => setNewEntry({ ...newEntry, match_type: e.target.value as BlacklistEntry['match_type'] })}
                className="input text-sm"
              >
                <option value="exact">Exact</option>
                <option value="contains">Contains</option>
                <option value="regex">Regex</option>
              </select>
              <Button
                size="sm"
                onClick={() => addEntryMutation.mutate(newEntry)}
                disabled={!newEntry.pattern}
                isLoading={addEntryMutation.isPending}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>
          </div>
        )}

        {/* Entries Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pattern</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Match</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                {canConfigure && <th className="px-4 py-2"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {blacklistData?.entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="px-4 py-2 text-sm text-gray-600">{entry.entry_type}</td>
                  <td className="px-4 py-2 text-sm font-mono">{entry.pattern}</td>
                  <td className="px-4 py-2 text-sm">{entry.match_type}</td>
                  <td className="px-4 py-2 text-sm text-gray-500">{entry.reason || '-'}</td>
                  {canConfigure && (
                    <td className="px-4 py-2">
                      <button
                        onClick={() => deleteEntryMutation.mutate(entry.id)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {(!blacklistData?.entries || blacklistData.entries.length === 0) && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No blacklist entries
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function Automation() {
  const { can, actualRole } = useRole();
  const [activeTab, setActiveTab] = useState<TabId>('scoring');

  // Check if user has automation:view permission
  if (!can('automation:view') && actualRole !== 'superadmin') {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Zap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-font-primary mb-2">
            Access Denied
          </h1>
          <p className="text-font-secondary">
            You don't have permission to view automation settings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-serif font-semibold text-font-primary flex items-center gap-2">
          <Zap className="w-6 h-6" />
          Automation
        </h1>
        <p className="text-font-secondary mt-1">
          Configure lead scoring, SLA management, and qualification rules
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-brand-primary text-brand-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4 inline-block mr-2" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="card p-6">
        {activeTab === 'scoring' && <ScoringTab />}
        {activeTab === 'sla' && <SLATab />}
        {activeTab === 'assignment' && <AssignmentTab />}
        {activeTab === 'qualification' && <QualificationTab />}
      </div>
    </div>
  );
}
